import { useTranslation } from 'next-i18next';
import { useMemo, type ReactNode } from 'react';

import css from '@/src/legal/presentation/views/cookies-policy-page-view/cookies-policy-page-view.css';

import type CookieDescriptionViewModel from '../../view-models/cookie-description-view-model';
import type { CookieDescriptionLifetimeItem } from '../../view-models/cookie-description-view-model';

export default function CookiesPolicyPageView() {
  const { t } = useTranslation('legal', { keyPrefix: 'views.cookies_policy' });
  const neccesaryCookies = useMemo<CookieDescriptionViewModel[]>(
    () => [
      {
        subgroup: {
          title: 'accounts.livechatinc.com',
          href: 'http://accounts.livechatinc.com/'
        },
        cookies: ['__lc_cid __lc_cst'],
        owner: 'third_party',
        lifetime: {
          value: 400,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'doubleclick.net',
          href: 'http://doubleclick.net/'
        },
        cookies: ['test_cookie'],
        owner: 'third_party',
        lifetime: {
          value: 1,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'google.com',
          href: 'http://google.com/'
        },
        cookies: ['_GRECAPTCHA'],
        owner: 'third_party',
        lifetime: {
          value: 180,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'gstatic.com',
          href: 'http://gstatic.com/'
        },
        cookies: ['rc::a rc::b rc::c rc::d-15#'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'hsforms.com',
          href: 'http://hsforms.com/?__hstc=43800058.722f257cf39e4a24d24ea8d908573823.1746526623970.1753260483996.1753340984701.31&__hssc=43800058.13.1753340984701&__hsfp=3458612402'
        },
        cookies: ['_cfuvid'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'hubspot.com',
          href: 'http://hubspot.com/?__hstc=43800058.722f257cf39e4a24d24ea8d908573823.1746526623970.1753260483996.1753340984701.31&__hssc=43800058.13.1753340984701&__hsfp=3458612402'
        },
        cookies: ['__cf_bm'],
        owner: 'third_party',
        lifetime: {
          value: 1,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'imgsct.cookiebot.com',
          href: 'http://imgsct.cookiebot.com/'
        },
        cookies: ['1.gif'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'linkedin.com',
          href: 'http://linkedin.com/'
        },
        cookies: ['li_gc'],
        owner: 'third_party',
        lifetime: {
          value: 180,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'utopicus.es',
          href: 'https://utopicus.es/'
        },
        cookies: ['CookieConsent'],
        owner: 'utopicus',
        lifetime: {
          value: 1,
          unit: 'year'
        }
      },
      {
        subgroup: {
          title: 'utopicus.spaces.nexudus.com',
          href: 'http://utopicus.spaces.nexudus.com/'
        },
        cookies: ['AWSALB', 'AWSALBCORS'],
        owner: 'third_party',
        lifetime: {
          value: 7,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'www.linkedin.com',
          href: 'http://www.linkedin.com/'
        },
        cookies: ['bscookie'],
        owner: 'third_party',
        lifetime: {
          value: 1,
          unit: 'year'
        }
      }
    ],
    []
  );
  const preferencesCookies = useMemo<CookieDescriptionViewModel[]>(
    () => [
      {
        subgroup: {
          title: 'accounts.livechatinc.com',
          href: 'http://accounts.livechatinc.com/'
        },
        cookies: ['__oauth_redirect_detector'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'cdn.livechatinc.com',
          href: 'http://cdn.livechatinc.com/'
        },
        cookies: ['#:state', '@@lc_ids'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'maps.googleapis.com',
          href: 'http://maps.googleapis.com/'
        },
        cookies: ['maps/gen_204'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      }
    ],
    []
  );
  const analysisCookies = useMemo<CookieDescriptionViewModel[]>(
    () => [
      {
        subgroup: {
          title: 'cdn.livechatinc.com',
          href: 'http://cdn.livechatinc.com/'
        },
        cookies: ['_livechat_has_visited'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'embed.ted.com',
          href: 'http://embed.ted.com/'
        },
        cookies: ['mnet_session_depth', 'muxData'],
        owner: 'third_party',
        lifetime: [
          {
            unit: 'session'
          },
          {
            value: 20,
            unit: 'year'
          }
        ]
      },
      {
        subgroup: {
          title: 'hsforms.com',
          href: 'http://hsforms.com/?__hstc=43800058.722f257cf39e4a24d24ea8d908573823.1746526623970.1753260483996.1753340984701.31&__hssc=43800058.13.1753340984701&__hsfp=3458612402'
        },
        cookies: ['__hssc __hssrc __hstc hubspotutk'],
        owner: 'third_party',
        lifetime: [
          {
            value: 1,
            unit: 'day'
          },
          {
            unit: 'session'
          },
          {
            value: 180,
            unit: 'day'
          }
        ]
      },
      {
        subgroup: {
          title: 'js-agent.newrelic.com',
          href: 'http://js-agent.newrelic.com/'
        },
        cookies: ['NRBA_SESSION'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'linkedin.com',
          href: 'http://linkedin.com/'
        },
        cookies: ['AnalyticsSyncHistory'],
        owner: 'third_party',
        lifetime: {
          value: 30,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'media.net',
          href: 'http://media.net/'
        },
        cookies: ['log', 'visitor-id'],
        owner: 'third_party',
        lifetime: [
          {
            unit: 'session'
          },
          {
            value: 1,
            unit: 'year'
          }
        ]
      },
      {
        subgroup: {
          title: 'ted.com',
          href: 'http://ted.com/'
        },
        cookies: ['_ga', '_gid'],
        owner: 'third_party',
        lifetime: [
          {
            value: 2,
            unit: 'year'
          },
          {
            value: 1,
            unit: 'day'
          }
        ]
      },
      {
        subgroup: {
          title: 'utopicus.es',
          href: 'https://utopicus.es/'
        },
        cookies: ['_ga_#'],
        owner: 'utopicus',
        lifetime: {
          value: 2,
          unit: 'year'
        }
      },
      {
        subgroup: {
          title: 'www.googletagmanager.com',
          href: 'http://www.googletagmanager.com/'
        },
        cookies: ['td'],
        owner: 'third_party',
        lifetime: [
          {
            unit: 'session'
          }
        ]
      }
    ],
    []
  );
  const advertisingCookies = useMemo<CookieDescriptionViewModel[]>(
    () => [
      {
        subgroup: {
          title: 'ads.linkedin.com',
          href: 'http://ads.linkedin.com/'
        },
        cookies: ['collect', 'collect/'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'connect.facebook.net',
          href: 'http://connect.facebook.net/'
        },
        cookies: ['lastExternalReferrer', 'lastExternalReferrerTime'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'doubleclick.net',
          href: 'http://doubleclick.net/'
        },
        cookies: ['IDE', 'pagead/landing'],
        owner: 'third_party',
        lifetime: [
          {
            value: 1,
            unit: 'year'
          },
          {
            unit: 'session'
          }
        ]
      },
      {
        subgroup: {
          title: 'google.com',
          href: 'http://google.com/'
        },
        cookies: ['ads/ga-audiences', 'pagead/1p-user-list/#'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'gstatic.com',
          href: 'http://gstatic.com/'
        },
        cookies: ['rc::f'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'hubspot.com',
          href: 'http://hubspot.com/?__hstc=43800058.722f257cf39e4a24d24ea8d908573823.1746526623970.1753260483996.1753340984701.31&__hssc=43800058.13.1753340984701&__hsfp=3458612402'
        },
        cookies: ['__ptq.gif'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'linkedin.com',
          href: 'http://linkedin.com/'
        },
        cookies: ['bcookie', 'li_sugr', 'lidc', 'UserMatchHistory'],
        owner: 'third_party',
        lifetime: [
          {
            value: 1,
            unit: 'year'
          },
          {
            value: 3,
            unit: 'month'
          },
          {
            value: 1,
            unit: 'day'
          },
          {
            value: 30,
            unit: 'day'
          }
        ]
      },
      {
        subgroup: {
          title: 'pa.tedcdn.com',
          href: 'http://pa.tedcdn.com/'
        },
        cookies: ['playerconfig'],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      },
      {
        subgroup: {
          title: 'scorecardresearch.com',
          href: 'http://scorecardresearch.com/'
        },
        cookies: ['UID'],
        owner: 'third_party',
        lifetime: {
          value: 400,
          unit: 'day'
        }
      },
      {
        subgroup: {
          title: 'utopicus.es',
          href: 'https://utopicus.es/'
        },
        cookies: ['_fbp', '_gcl_au'],
        owner: 'utopicus',
        lifetime: {
          value: 3,
          unit: 'month'
        }
      },
      {
        subgroup: {
          title: 'youtube.com',
          href: 'http://youtube.com/'
        },
        cookies: [
          'LAST_RESULT_ENTRY_KEY',
          'LogsDatabaseV2:V#||LogsRequestsStore',
          'nextId',
          'remote_sid',
          'requests',
          'ServiceWorkerLogsDatabase#SWHealthLog',
          'TESTCOOKIESENABLED',
          'VISITOR_INFO1_LIVE',
          'VISITOR_PRIVACY_METADATA',
          'YSC',
          'yt.innertube::nextId',
          'YtIdbMeta#databases',
          'yt-remote-cast-available',
          'yt-remote-cast-installed',
          'yt-remote-connected-devices',
          'yt-remote-device-id',
          'yt-remote-fast-check-period',
          'yt-remote-session-app',
          'yt-remote-session-name'
        ],
        owner: 'third_party',
        lifetime: {
          unit: 'session'
        }
      }
    ],
    []
  );
  const renderSection = (
    key:
      | 'what_are_cookies_section'
      | 'cookie_types_section'
      | 'cookies_config_section'
      | 'cookies_update_section'
      | 'cookies_list_section'
      | 'necessary_cookies_section'
      | 'preferences_cookies_section'
      | 'advertising_cookies_section'
      | 'analysis_cookies_section'
      | 'advertising_cookies_section',
    titleTag: 'h3' | 'h4' = 'h3',
    appendNode: ReactNode = null
  ) => {
    const TitleTag = titleTag;

    return (
      <div className={css.section}>
        <TitleTag>{t(`${key}.title`)}</TitleTag>

        {t(`${key}.paragraphs`, { returnObjects: true }).map((paragraph, index) => (
          <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }}></p>
        ))}

        {appendNode}
      </div>
    );
  };
  const renderCookiesTable = (cookies: CookieDescriptionViewModel[]) => {
    return (
      <div className={css.cookiesTableWrapper}>
        <table className={css.cookiesTable}>
          <thead>
            <tr>
              <th className={css.cookiesTableCell}>{t('cookies_table.headers.subgroup')}</th>
              <th className={css.cookiesTableCell}>{t('cookies_table.headers.cookies')}</th>
              <th className={css.cookiesTableCell}>{t('cookies_table.headers.owner')}</th>
              <th className={css.cookiesTableCell}>{t('cookies_table.headers.lifetime')}</th>
            </tr>
          </thead>
          <tbody>
            {cookies.map((cookie, index) => {
              const getLifetimeString = (lifetimeItem: CookieDescriptionLifetimeItem) =>
                lifetimeItem.unit === 'session'
                  ? t('cookies_table.lifetime_unit.session')
                  : t(`cookies_table.lifetime_unit.${lifetimeItem.unit}`, {
                    count: lifetimeItem.value
                  });
              const cookiesString = cookie.cookies.join('</br>');
              const ownerString = t(`cookies_table.owner.${cookie.owner}`);
              const lifetimeString = Array.isArray(cookie.lifetime)
                ? cookie.lifetime.map(getLifetimeString).join('</br>')
                : getLifetimeString(cookie.lifetime);

              return (
                <tr key={index}>
                  <td className={css.cookiesTableCell}>
                    <a
                      href={cookie.subgroup.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css.cookiesTableLink}
                    >
                      {cookie.subgroup.title}
                    </a>
                  </td>
                  <td
                    className={css.cookiesTableCell}
                    dangerouslySetInnerHTML={{ __html: cookiesString }}
                  ></td>
                  <td className={css.cookiesTableCell}>{ownerString}</td>
                  <td
                    className={css.cookiesTableCell}
                    dangerouslySetInnerHTML={{ __html: lifetimeString }}
                  ></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={css.page}>
      <h1 className={css.title}>{t('title')}</h1>

      <p>
        <strong className={css.subtitle}>{t('subtitle')}</strong>
      </p>

      {renderSection('what_are_cookies_section')}
      {renderSection('cookie_types_section')}

      {renderSection(
        'cookies_list_section',
        'h3',
        <>
          {renderSection('necessary_cookies_section', 'h4', renderCookiesTable(neccesaryCookies))}
          {renderSection(
            'preferences_cookies_section',
            'h4',
            renderCookiesTable(preferencesCookies)
          )}
          {renderSection('analysis_cookies_section', 'h4', renderCookiesTable(analysisCookies))}
          {renderSection(
            'advertising_cookies_section',
            'h4',
            renderCookiesTable(advertisingCookies)
          )}
        </>
      )}

      {renderSection('cookies_config_section')}
      {renderSection('cookies_update_section')}
    </div>
  );
}
